import { useEffect, useState } from "react";

import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EditMode } from "@/Common/Models/Patient/PatientEnums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { AddRelationPopup } from "@/Patient/Components/Profile/Relations/AddRelationPopup";
import { RelationEdit } from "@/Patient/Components/Profile/Relations/RelationEdit";
import { RelationView } from "@/Patient/Components/Profile/Relations/RelationView";
import { relativeStore } from "../Stores/RelativeStore";

import styles from "@/Patient/Components/Profile/Relations/Relations.module.scss";

interface IRelationsProps {
    view: EditMode;
}

function Relations(props: IRelationsProps): JSX.Element {
    const [showRelationshipPopup, setShowRelationshipPopup] = useState(false);
    const [relations, setRelations] = useState(relativeStore.GetCurrent().Relationships);
    const { getText } = useTranslation();

    const relativeStoreListenerRef = relativeStore.AddChangeListener(() => {
        setRelations(relativeStore.GetCurrent().Relationships);
    }, this, "relations-listener");

    useEffect(() => {
        $(window).trigger("resize");
        return () => {
            relativeStore.RemoveListener(relativeStoreListenerRef);
        };
    });

    const onAddRelation = (linkedContact: LinkedContact): void => {
        const newRelations = [...relations];
        newRelations.push(linkedContact);
        onSaveRelation(newRelations);
    };

    const onRemoveRelation = (linkedContact: LinkedContact): void => {
        const newRelations = relations.filter(r => r.Id !== linkedContact.Id);
        setRelations(newRelations);
    };

    const onSaveRelation = (relations: LinkedContact[]): void => {
        relativeStore.actions.updateRelations(relations);
        setShowRelationshipPopup(false);
    };

    const onChangeRelation = (index: number, mutation: (lc: LinkedContact) => void): void => {
        const newRelations = [...relations];
        mutation(newRelations[index]);
        setRelations(newRelations);
    };

    const existingRelationsContactIds = (): string[] => {
        const contactsIds = relations.map(r => r.Individual.Contact_id);
        contactsIds.push(relativeStore.GetCurrent().ContactId);
        return contactsIds;
    };

    return (
        <div className="tabComponent">
            <div className="tabComponent-main patient-prof">
                {showRelationshipPopup && (
                    <AddRelationPopup
                        onClose={() => setShowRelationshipPopup(false)}
                        onSave={linkedContact => onAddRelation(linkedContact)}
                        existingRelationsContactIds={existingRelationsContactIds()}
                    />
                )}

                <div className={`block separated ${styles.relations}`}>
                    <header>
                        <div className={styles.header + " heading"}>
                            <h3 className="thin-font">{getText("RelationsLabel")}</h3>
                            {patientStore.CanEditPatientRelationship && (
                                <a onClick={() => setShowRelationshipPopup(true)}>[+] {getText("Relations_AddRelationLabel")}</a>
                            )}
                        </div>
                    </header>
                    <div className={styles.bodyList}>
                        <div className={styles.list}>
                            {relations?.map((linkedContact, index) => (
                                <>
                                    {props.view === EditMode.Edit && (
                                        <RelationEdit
                                            index={index}
                                            linkedContact={linkedContact}
                                            onRemoveRelation={() => onRemoveRelation(linkedContact)}
                                            onChanged={mutation => onChangeRelation(index, mutation)}
                                            key={linkedContact.Id}
                                        />
                                    )}
                                    {props.view !== EditMode.Edit && (
                                        <RelationView
                                            linkedContact={linkedContact}
                                            key={linkedContact.Id}
                                        />
                                    )}
                                </>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className="tabComponent-footer ws-subfooter patient-profile">
                <div className="tb patient-profile-footer">
                    <div className="tbc pull-right">
                        {props.view === EditMode.View
                            ? (
                                <ButtonControl
                                    className="btn btn-primary pull-right"
                                    onClick={() => sessionStore.dispatch(LocalOperations.Relative.EditProfile)}
                                    label={getText("EDITLabel")}
                                    disabled={!relations.any()}
                                />
                            )
                            : (
                                <>
                                    <ButtonControl
                                        className="btn btn-default push-right-5"
                                        onClick={() => sessionStore.dispatch(LocalOperations.Relative.CancelProfileEdition)}
                                        label={getText("CancelLabel")}
                                    />
                                    <ButtonControl
                                        className="btn btn-primary"
                                        label={getText("SaveLabel")}
                                        onClick={() => onSaveRelation(relations)}
                                    />
                                </>
                            )
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

export { Relations };
